import { useMemo } from 'react';

import useAuth from '@/auth/application/useAuth';

import { type ApprovalPermissionPolicy } from '@/userManagement/domain/constants/approvalPermissionPolicies';

import { type RolePermissionPolicy, jwtIncludesRolePolicy } from '@/userManagement/domain/constants/rolePermissionPolicies';

import {
  AUDIT_PERMISSION_POLICIES,
  type AuditPermissionPolicy,
  jwtIncludesAuditPolicy
} from '@/userManagement/domain/constants/auditPermissionPolicies';

import { type UserPermissionPolicy } from '@/userManagement/domain/constants/userPermissionPolicies';

import { type ToolAccessPermissionPolicy } from '@/userManagement/domain/constants/toolAccessPermissionPolicies';

import { type SettingsPermissionPolicy } from '@/userManagement/domain/constants/settingsPermissionPolicies';

import { BULK_UPLOAD_POLICIES, type BulkUploadPermissionPolicy } from '@/userManagement/domain/constants/bulkUploadPermissionPolicies';

import { type AccessReviewPermissionPolicy } from '@/userManagement/domain/constants/accessReviewPermissionPolicies';

import { parseJwtPermissionPolicies } from '@/userManagement/application/utils/parseJwtPermissionPolicies';

import { useOperatorPermissions } from './useOperatorPermissions';

export type UmPermissionPolicy =
  | ApprovalPermissionPolicy
  | RolePermissionPolicy
  | AuditPermissionPolicy
  | UserPermissionPolicy
  | ToolAccessPermissionPolicy
  | SettingsPermissionPolicy
  | BulkUploadPermissionPolicy
  | AccessReviewPermissionPolicy;

function policyGranted(policies: Set<string>, policy: UmPermissionPolicy): boolean {
  if (policy === 'RolesCreate' || policy === 'RolesEdit') {
    return jwtIncludesRolePolicy(policies, policy);
  }

  if (policy === AUDIT_PERMISSION_POLICIES.REVIEW_OTHERS) {
    return jwtIncludesAuditPolicy(policies, policy);
  }

  // BulkUpload is often seeded Read-only; operator-permissions only returns Write-bit features.
  // Super Admins get BulkUploadInitialImport (Write) — treat that as module access for the tab/export.
  if (policy === BULK_UPLOAD_POLICIES.BULK_UPLOAD) {
    return policies.has(BULK_UPLOAD_POLICIES.BULK_UPLOAD) || policies.has(BULK_UPLOAD_POLICIES.BULK_UPLOAD_INITIAL_IMPORT);
  }

  return policies.has(policy);
}

/**
 * Returns whether the current user has the given UM permission policy.
 * Prefers server-driven operator permissions; falls back to JWT `PermissionPolicies` when the API is unavailable.
 */
export function useHasPermission(policy: UmPermissionPolicy): boolean {
  const { getToken } = useAuth();
  const { featureKeys, isReadOnlyOperator, isSuccess } = useOperatorPermissions();

  return useMemo(() => {
    if (isSuccess && featureKeys !== undefined) {
      const granted = policyGranted(new Set(featureKeys), policy);
      if (granted) {
        return true;
      }

      if (isReadOnlyOperator) {
        return false;
      }
    }

    if (isReadOnlyOperator && isSuccess) {
      return false;
    }

    const { policies, hasExplicitPolicies } = parseJwtPermissionPolicies(getToken());
    if (!hasExplicitPolicies) {
      return false;
    }

    return policyGranted(policies, policy);
  }, [featureKeys, getToken, isReadOnlyOperator, isSuccess, policy]);
}
